import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { MEDIA_BASE_URL } from "@/lib/apiConfig";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converte path relativo ou URL absoluta de mídia.
 * Se a API devolver o endpoint S3 privado (*.r2.cloudflarestorage.com/bucket/...),
 * reescreve para VITE_MEDIA_URL (domínio público R2) quando configurado.
 */
export function resolveMediaUrl(path?: string | null): string | null {
  if (!path) return null;

  const base = MEDIA_BASE_URL.replace(/\/$/, "");
  const isAbsolute = path.startsWith("http://") || path.startsWith("https://");

  if (isAbsolute) {
    if (!base || base.includes("r2.cloudflarestorage.com")) return path;
    try {
      const url = new URL(path);
      if (!url.hostname.endsWith(".r2.cloudflarestorage.com")) return path;
      // /{bucket}/articles/... → /articles/...
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length < 2) return path;
      const objectPath = parts.slice(1).join("/");
      return `${base}/${objectPath}${url.search}`;
    } catch {
      return path;
    }
  }

  if (base) {
    return `${base}${path.startsWith("/") ? path : `/${path}`}`;
  }
  return path.startsWith("/") ? path : `/${path}`;
}

export function formatDate(value?: string | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  // diffMs > 0 = futuro; < 0 = passado. RelativeTimeFormat: negativo = passado.
  const diffMs = date.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const sign = diffMs < 0 ? -1 : 1;
  const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

  const minutes = Math.round(absMs / 60_000);
  if (minutes < 60) return rtf.format(sign * minutes, "minute");

  const hours = Math.round(absMs / 3_600_000);
  if (hours < 24) return rtf.format(sign * hours, "hour");

  const days = Math.round(absMs / 86_400_000);
  if (days < 7) return rtf.format(sign * days, "day");

  return formatDate(value);
}

export function formatPrice(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(num || 0);
}
